import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Lock } from 'lucide-react';
import { LoyaltyTest } from '../types';
import { notifyAdminOfPendingSession } from '../lib/adminAlert';

// Shown on the sender's results page once the recipient has flipped on
// "let them ask one more thing". Visible to every sender regardless of
// plan (that's the whole point — it's the upsell moment) but only a
// Premium sender can actually write and send the question. Free senders
// see the same "they said yes" banner with the reply locked behind an
// upgrade prompt.
export default function SendFollowUp({
  test,
  isPremium,
  onSent,
  onUpgrade,
  initialQuestion,
  initialQuestionKey,
}: {
  test: LoyaltyTest;
  isPremium: boolean;
  onSent: (updated: LoyaltyTest) => void;
  onUpgrade: () => void;
  initialQuestion?: string;
  /** bump this to re-apply the same suggestion after the user edited the box */
  initialQuestionKey?: number;
}) {
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (initialQuestion) setQuestion(initialQuestion.slice(0, 280));
  }, [initialQuestion, initialQuestionKey]);

  // Already used their one follow-up on this session, or the recipient
  // never opted in — nothing to show.
  if (!test.follow_up_enabled || test.follow_up_used) return null;

  const send = async () => {
    const text = question.trim();
    if (!text || sending || !isPremium || !test.id) return;
    setSending(true);
    setError(false);
    try {
      const newQuestion = { id: Math.random().toString(36).substr(2, 9), text };
      const updatedQuestions = [...(test.questions ?? []), newQuestion];

      // No auto-send for follow-ups on any platform, including WhatsApp,
      // until a follow-up message template is submitted to and approved by
      // Meta. Every follow-up goes back through the admin queue for a
      // manual dispatch for now — admin_notified_at is cleared so the
      // operator gets pinged again. getDispatchMessage() picks up the
      // recap automatically once questions/answers are back out of sync.
      const { error: updateError } = await supabase
        .from('tests')
        .update({
          questions: updatedQuestions,
          follow_up_used: true,
          status: 'pending',
          admin_notified_at: null,
        })
        .eq('id', test.id);
      if (updateError) throw updateError;

      notifyAdminOfPendingSession(test.id);

      onSent({ ...test, questions: updatedQuestions, follow_up_used: true, status: 'pending' });
      setQuestion('');
    } catch (e) {
      console.error('SendFollowUp failed:', e);
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div id="follow-up-box" className="mt-6 p-5 rounded-2xl bg-brand-card border border-brand-accent/20 scroll-mt-24">
      <p className="text-[10px] font-black uppercase tracking-widest text-brand-accent mb-1">
        They said yes 👀
      </p>
      <p className="text-xs text-zinc-500 mb-4">
        {test.partner_name || 'They'} said you can ask them one more anonymous question. You only get one — make it count.
      </p>

      {isPremium ? (
        <>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            maxLength={280}
            rows={3}
            placeholder="Type your one follow-up question..."
            className="w-full box-border p-4 rounded-xl text-sm bg-brand-bg border border-brand-border outline-none resize-none mb-3"
            style={{ color: 'var(--brand-text)', fontFamily: 'inherit' }}
          />
          {error && (
            <p className="text-xs font-bold text-brand-accent mb-3">
              Couldn't send that — check your connection and try again.
            </p>
          )}
          <button
            onClick={send}
            disabled={!question.trim() || sending}
            className="btn-primary w-full py-3 text-sm font-bold uppercase tracking-widest disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send follow-up question'}
          </button>
        </>
      ) : (
        <div className="relative">
          <textarea
            disabled
            rows={3}
            placeholder="Type your one follow-up question..."
            className="w-full box-border p-4 rounded-xl text-sm bg-brand-bg border border-brand-border resize-none mb-3 opacity-40 blur-[1px] pointer-events-none"
            style={{ color: 'var(--brand-text)', fontFamily: 'inherit' }}
          />
          <button
            onClick={onUpgrade}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-black uppercase tracking-widest hover:bg-amber-500/20 transition-colors cursor-pointer"
          >
            <Lock className="w-3.5 h-3.5" />
            Upgrade to send your follow-up
          </button>
        </div>
      )}
    </div>
  );
}
